import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { handleProviderClick, getProviderErrorMessage } from '../../lib/providerSso';
import { isBookrProvider } from '../../lib/bookrSso';
import { isHighlightsLibraryProvider } from '../../lib/highlightsSso';
import { isOpenElmsProvider } from '../../lib/openElmsSso';
import { useRealtimeAccessGuard } from '../../lib/useRealtimeAccessGuard';
import AccessGuardOverlay from '../../components/AccessGuardOverlay/AccessGuardOverlay';
import './ProviderModal.css';
import Logo from '../../assets/Logo.svg';

// Simple in-session cache for providers to avoid refetching on navigation
const PROVIDERS_CACHE_PREFIX = 'mds_providers_cache_';
const PROVIDERS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const ADS_CACHE_KEY = 'mds_ads_cache_v1';
const ADS_ENABLED_KEY = 'mds_ads_enabled_v1';

const ProviderModal = () => {
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);

  // Initialize Ads state from cache if available to prevent flash
  const [ads, setAds] = useState(() => {
    try {
      const cached = sessionStorage.getItem(ADS_CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) { /* parse error */ }
    return [];
  });

  const [adsLoading, setAdsLoading] = useState(() => {
    // If we have cached ads, don't show loading state initially
    return !sessionStorage.getItem(ADS_CACHE_KEY);
  });

  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const carouselTimerRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [, setLoadingUser] = useState(true);
  const [error, setError] = useState(null);
  const [schoolName, setSchoolName] = useState(null);
  const [showBookrNextWarning, setShowBookrNextWarning] = useState(false);
  const [pendingBookrProvider, setPendingBookrProvider] = useState(null);

  // Initialize Ads Enabled state from cache
  const [adsEnabled, setAdsEnabled] = useState(() => {
    try {
      const cached = sessionStorage.getItem(ADS_ENABLED_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) { /* parse error */ }
    return true; // Default
  });

  const [adsEnabledLoading, setAdsEnabledLoading] = useState(() => {
    return !sessionStorage.getItem(ADS_ENABLED_KEY);
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Realtime access guard: kick user out if their account is deactivated or school subscription becomes inactive.
  const { blockInfo, acknowledge } = useRealtimeAccessGuard({
    userId: currentUser?.id || (() => {
      try {
        const raw = sessionStorage.getItem('currentUser');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.id || null;
      } catch {
        return null;
      }
    })(),
    schoolId: currentUser?.app_metadata?.school_id || (() => {
      try {
        const raw = sessionStorage.getItem('currentUser');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.school_id || null;
      } catch {
        return null;
      }
    })(),
  });

  // ... (fetchCurrentUser code remains the same)

  // ... (fetchProviders code remains the same)

  const fetchAdsEnabledAndAds = async () => {
    try {
      // Only set loading if we don't have data, to allow background refresh without flash
      if (!sessionStorage.getItem(ADS_ENABLED_KEY)) setAdsEnabledLoading(true);
      if (!sessionStorage.getItem(ADS_CACHE_KEY)) setAdsLoading(true);

      // Fetch ads enabled setting
      const { data, error } = await supabase
        .from('system_settings')
        .select('ads_enabled')
        .eq('id', 1)
        .single();

      let isEnabled = true;
      if (error && error.code !== 'PGRST116') {

        // Default to true if error
      } else {
        isEnabled = data?.ads_enabled !== false;
      }

      setAdsEnabled(isEnabled);
      sessionStorage.setItem(ADS_ENABLED_KEY, JSON.stringify(isEnabled));

      // Fetch ads in parallel (regardless of enabled status)
      const { data: adsData, error: adsError } = await supabase
        .from('ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (adsError) {

        // Don't clear ads if we have cached ones and just failed to refresh? 
        // Or clear them? For safety let's keep cache if network fails, or empty if we prefer fresh.
        // Let's set to empty array on error to be safe.
        setAds([]);
        sessionStorage.removeItem(ADS_CACHE_KEY);
      } else {

        setAds(adsData || []);
        sessionStorage.setItem(ADS_CACHE_KEY, JSON.stringify(adsData || []));
      }
    } catch (err) {

      // Keep existing state or default? 
      // If we are initializing, we might want defaults.
    } finally {
      setAdsEnabledLoading(false);
      setAdsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {

    try {
      setLoadingUser(true);

      // Get user data from sessionStorage (set by Hero component)
      const userDataStr = sessionStorage.getItem('currentUser');

      if (!userDataStr) {

        setError('Please enter your User ID on the home page to access providers');
        // Force redirect to landing page
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
        return;
      }

      const userData = JSON.parse(userDataStr);




      // Create a user object that matches the expected format
      const user = {
        id: userData.id,
        email: userData.email,
        user_metadata: {
          full_name: userData.full_name || `${userData.first_name} ${userData.last_name}`,
          first_name: userData.first_name,
          last_name: userData.last_name,
          grade: userData.grade, // Add grade information
        },
        app_metadata: {
          role: userData.role,
          school_id: userData.school_id,
          school: userData.schools,
        }
      };

      setCurrentUser(user);

      // Extract school name from user data
      const school = userData.schools;
      if (school?.name) {
        setSchoolName(school.name);
      } else if (userData.school_id) {
        // Fetch school name if not in session
        const { data: schoolData } = await supabase
          .from('schools')
          .select('name')
          .eq('id', userData.school_id)
          .single();
        if (schoolData?.name) {
          setSchoolName(schoolData.name);
        }
      }


    } catch (err) {

      setError('Invalid user session. Please enter your User ID again.');
    } finally {
      setLoadingUser(false);

    }
  };

  const fetchProviders = async (forceRefresh = false) => {
    try {
      setProvidersLoading(true);

      if (!currentUser) {
        setProviders([]);
        return;
      }

      // Try fast path: use cached providers for this user if still fresh and matching context
      const cacheKey = `${PROVIDERS_CACHE_PREFIX}${currentUser.id || 'anon'}`;

      // If forceRefresh is true, clear cache and skip cache check entirely
      if (forceRefresh) {
        sessionStorage.removeItem(cacheKey);
      } else {
        // Only check cache if not forcing refresh
        const cachedStr = sessionStorage.getItem(cacheKey);
        if (cachedStr) {
          try {
            const cached = JSON.parse(cachedStr);
            const now = Date.now();
            const isFresh = cached.timestamp && (now - cached.timestamp) < PROVIDERS_CACHE_TTL_MS;
            const cacheMatchesContext =
              cached.meta &&
              cached.meta.schoolId === currentUser.app_metadata?.school_id;

            if (isFresh && cacheMatchesContext && Array.isArray(cached.providers)) {
              // Verify cached providers are still active by checking their current status
              // This ensures deactivated providers don't show up from stale cache
              const cachedProviderIds = cached.providers.map(p => p.id).filter(Boolean);
              if (cachedProviderIds.length > 0) {
                const { data: activeProviders } = await supabase
                  .from('providers')
                  .select('id, is_active')
                  .in('id', cachedProviderIds)
                  .eq('is_active', true);

                const activeProviderIds = new Set((activeProviders || []).map(p => p.id));
                const filteredCachedProviders = cached.providers.filter(p => activeProviderIds.has(p.id));

                // Only use cache if all providers are still active
                if (filteredCachedProviders.length === cached.providers.length) {

                  setProviders(cached.providers);
                  setProvidersLoading(false);
                  return;
                } else if (filteredCachedProviders.length > 0) {
                  // Some providers were deactivated, use filtered cache and update it

                  setProviders(filteredCachedProviders);
                  // Update cache with filtered providers
                  try {
                    const updatedPayload = {
                      ...cached,
                      providers: filteredCachedProviders,
                      timestamp: Date.now(), // Update timestamp
                    };
                    sessionStorage.setItem(cacheKey, JSON.stringify(updatedPayload));
                  } catch (e) {

                  }
                  setProvidersLoading(false);
                  return;
                } else {
                  // All cached providers were deactivated, fetch fresh

                  sessionStorage.removeItem(cacheKey);
                }
              } else {
                // No provider IDs in cache, use it as-is (shouldn't happen but safe fallback)

                setProviders(cached.providers);
                setProvidersLoading(false);
                return;
              }
            }
          } catch (e) {

          }
        }
      }

      // First, get the current user's school information
      if (!currentUser?.app_metadata?.school_id) {


        setProviders([]);
        return;
      }

      const schoolId = currentUser.app_metadata.school_id;

      // Get school's accessible providers
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('accessible_providers')
        .eq('id', schoolId)
        .single();

      if (schoolError) {

        setProviders([]);
        return;
      }


      if (!schoolData?.accessible_providers || !Array.isArray(schoolData.accessible_providers)) {


        setProviders([]);
        return;
      }

      const accessibleProviderKeys = schoolData.accessible_providers;


      // If accessible_providers contains provider IDs (UUIDs), we need to fetch by ID
      // If it contains provider keys (strings), we fetch by key
      const isProviderIds = accessibleProviderKeys.every(key =>
        typeof key === 'string' && key.includes('-') && key.length === 36
      );

      let query;
      if (isProviderIds) {
        // accessible_providers contains provider IDs (UUIDs)

        query = supabase
          .from('providers')
          .select('id, name, logo, website, key, is_active, supported_grades, created_at')
          .eq('is_active', true)
          .in('id', accessibleProviderKeys)
          .order('order_index', { ascending: true })
          .order('created_at', { ascending: false });
      } else {
        // accessible_providers contains provider keys (strings)

        query = supabase
          .from('providers')
          .select('id, name, logo, website, key, is_active, supported_grades, created_at')
          .eq('is_active', true)
          .in('key', accessibleProviderKeys)
          .order('order_index', { ascending: true })
          .order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (forceRefresh) {

        if (data) {

        }
      }

      if (error) {

        setProviders([]);
        return;
      }


      // Use logo URL directly; provide safe fallbacks
      // Note: Grade filtering has been removed - all school users can access all providers for their school
      const transformed = (data || []).map((p) => ({
        id: p.id,
        name: p.name || 'Provider',
        logo: p.logo || '',         // expected to be a full URL
        website: p.website || '',
        key: p.key || '',           // provider key for SSO identification
      }));


      setProviders(transformed);

      // Persist to session cache for fast subsequent loads
      try {
        const payload = {
          providers: transformed,
          timestamp: Date.now(),
          meta: {
            schoolId,
          },
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(payload));
      } catch (e) {

      }
    } catch (err) {

      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  };



  // Carousel functionality for multiple ads (15s) and reset on manual change
  useEffect(() => {
    // Clear any existing timer
    if (carouselTimerRef.current) {
      clearInterval(carouselTimerRef.current);
    }

    if (ads.length > 1) {
      carouselTimerRef.current = setInterval(() => {
        setCurrentAdIndex((prevIndex) =>
          prevIndex === ads.length - 1 ? 0 : prevIndex + 1
        );
      }, 15000); // Switch every 15 seconds
    }

    return () => {
      if (carouselTimerRef.current) {
        clearInterval(carouselTimerRef.current);
      }
    };
  }, [ads.length, currentAdIndex]);

  // Check if provider is BOOKR NEXT
  const isBookrNextProvider = (provider) => {
    if (!provider) return false;
    const name = (provider.name || '').toLowerCase();
    const key = (provider.key || '').toLowerCase();
    return (name.includes('bookr') && name.includes('next')) ||
      (key.includes('bookr') && key.includes('next')) ||
      key === 'bookr-next' || key === 'bookr_next';
  };

  // Check if BOOKR NEXT warning has been shown before
  const hasSeenBookrNextWarning = () => {
    try {
      return localStorage.getItem('bookr_next_warning_seen') === 'true';
    } catch {
      return false;
    }
  };

  // Mark BOOKR NEXT warning as seen
  const markBookrNextWarningSeen = () => {
    try {
      localStorage.setItem('bookr_next_warning_seen', 'true');
    } catch (err) {

    }
  };

  // Provider click handler
  const handleProviderClickWrapper = async (provider) => {



    if (!currentUser) {

      setError('Please log in to access providers');
      return;
    }

    // Check if this is BOOKR NEXT and if warning hasn't been shown
    if (isBookrNextProvider(provider) && !hasSeenBookrNextWarning()) {
      setPendingBookrProvider(provider);
      setShowBookrNextWarning(true);
      return;
    }


    setError(null); // Clear any previous errors

    try {

      await handleProviderClick(provider, currentUser.id, (errorMessage) => {

        setError(getProviderErrorMessage(errorMessage, provider?.key));
      }, navigate);

    } catch (err) {

      setError('An unexpected error occurred. Please try again.');
    }


  };

  // Handle BOOKR NEXT warning acknowledgment
  const handleBookrNextWarningAcknowledge = async () => {
    markBookrNextWarningSeen();
    setShowBookrNextWarning(false);

    if (pendingBookrProvider) {
      const provider = pendingBookrProvider;
      setPendingBookrProvider(null);

      // Now proceed with the provider click
      setError(null);
      try {
        await handleProviderClick(provider, currentUser.id, (errorMessage) => {
          setError(getProviderErrorMessage(errorMessage, provider?.key));
        }, navigate);
      } catch (err) {

        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    // Fetch both ads settings and ads data in parallel
    fetchAdsEnabledAndAds();
  }, []);

  // Set up real-time subscription for providers table changes
  useEffect(() => {
    if (!currentUser?.app_metadata?.school_id) return;

    const schoolId = currentUser.app_metadata.school_id;
    const userId = currentUser.id || 'anon';

    // Subscribe to changes in the providers table
    const channel = supabase
      .channel(`providers-changes-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'providers',
        },
        async (payload) => {

          const changedProviderId = payload.new?.id || payload.old?.id;

          // Check if this provider is relevant to the current user's school
          const { data: schoolData } = await supabase
            .from('schools')
            .select('accessible_providers')
            .eq('id', schoolId)
            .single();

          const accessibleProviderIds = schoolData?.accessible_providers || [];
          const isRelevant = Array.isArray(accessibleProviderIds) && accessibleProviderIds.includes(changedProviderId);

          if (!isRelevant) {

            return;
          }

          // Clear cache to force fresh data
          const cacheKey = `${PROVIDERS_CACHE_PREFIX}${userId}`;
          sessionStorage.removeItem(cacheKey);

          if (payload.eventType === 'UPDATE') {
            const oldIsActive = payload.old?.is_active;
            const newIsActive = payload.new?.is_active;

            // If is_active status changed, update the list immediately
            if (oldIsActive !== newIsActive) {

              if (newIsActive === false) {
                // Provider was deactivated - remove from list immediately

                setProviders(prevProviders =>
                  prevProviders.filter(p => p.id !== changedProviderId)
                );
              } else {
                // Provider was activated - refetch to get full provider details



                // Force refresh to bypass cache and get the newly activated provider
                fetchProviders(true).catch(err => {

                });
              }
            } else {
              // Other fields changed (name, logo, etc.), refetch to get updated data

              fetchProviders(true);
            }
          } else if (payload.eventType === 'DELETE') {
            // Provider deleted - remove from list immediately

            setProviders(prevProviders =>
              prevProviders.filter(p => p.id !== changedProviderId)
            );
          } else if (payload.eventType === 'INSERT') {
            // New provider added - check if it's active and accessible
            if (payload.new.is_active === true && accessibleProviderIds.includes(payload.new.id)) {

              fetchProviders(true);
            }
          }
        }
      )
      .subscribe((status) => {

        if (status === 'SUBSCRIBED') {

        } else if (status === 'CHANNEL_ERROR') {

        }
      });

    // Cleanup subscription on unmount
    return () => {

      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Fetch providers after current user is loaded
  useEffect(() => {
    if (currentUser) {
      fetchProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleNavClick = (path) => navigate(path);
  const normalizeUrl = (url) => {
    if (!url) return '';
    try {
      // If it already has a scheme, keep it
      if (/^https?:\/\//i.test(url)) return url;
      return `https://${url}`;
    } catch {
      return url;
    }
  };

  const handleAdClick = (ad) => {
    // 1) Open FIRST (keeps user gesture so popups aren't blocked)
    const url = normalizeUrl(ad.action_url);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    // 2) Atomically increment click count using RPC function
    if (ad?.id) {
      supabase
        .rpc('increment_ad_click_count', { ad_id: ad.id })
        .then(() => { })
        .catch(() => {});
    }
  };

  // Removed "Providers" item; "Home" now routes to /providers
  const navItems = [
    { label: 'Home', path: '/home' },
    { label: 'Calendar', path: '/calendar' },
    { label: 'Profile', path: '/profile' },
  ];
  return (
    <div className="provider-page">
      {blockInfo && (
        <AccessGuardOverlay
          title={blockInfo.title}
          message={blockInfo.message}
          onAcknowledge={acknowledge}
        />
      )}
      {/* Header - match dashboard header style */}
      <header className="provider-page-header">
        <div className="provider-page-header-inner">
          <div
            className="header-left"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          >
            <img src={Logo} alt="MyDigitalSchool" className="header-logo" />
            <h1>My Digital Schools</h1>
          </div>
          <div className="header-right">
            <nav className="provider-page-nav">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  className={`provider-page-nav-item ${location.pathname === item.path ? 'active' : ''
                    }`}
                  onClick={() => handleNavClick(item.path)}
                >
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="page-content-wrapper">
        {schoolName && (
          <div className="school-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span>{schoolName}</span>
          </div>
        )}
        {/* Hero Section - Only show if ads are enabled */}
        {!adsEnabledLoading && adsEnabled && (
          <div className="provider-page-hero">
            <div className="provider-page-hero-inner">
              <div className="provider-page-hero-content">
                {adsLoading ? (
                  <>
                    <div className="skeleton-bar skeleton-title"></div>
                    <div className="skeleton-bar skeleton-subtitle"></div>
                    <div className="skeleton-bar skeleton-button"></div>
                  </>
                ) : (
                  (() => {
                    const currentAd = ads && ads.length > 0 ? ads[currentAdIndex] : null;
                    const title = currentAd?.title && currentAd.title.trim().length > 0
                      ? currentAd.title
                      : 'Access your account immediately and start learning now!';
                    const subtitle = currentAd?.subtitle && currentAd.subtitle.trim().length > 0
                      ? currentAd.subtitle
                      : 'Direct Access to available providers';
                    const buttonText = currentAd?.button_text && currentAd.button_text.trim().length > 0
                      ? currentAd.button_text
                      : '';
                    return (
                      <>
                        <h1 className="provider-page-title">{title}</h1>
                        <p className="provider-page-subtitle">{subtitle}</p>
                        {buttonText && currentAd?.id && (
                          <button
                            className="provider-page-join-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = normalizeUrl(currentAd?.action_url);
                              if (url) {
                                window.open(url, '_blank', 'noopener,noreferrer');
                                // Atomically increment click count using RPC function
                                supabase
                                  .rpc('increment_ad_click_count', { ad_id: currentAd.id })
                                  .then(() => { })
                                  .catch(() => {});
                              }
                            }}
                          >
                            {buttonText}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '10px' }}>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                              <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                          </button>
                        )}
                      </>
                    );
                  })()
                )}
              </div>

              {/* Ad Display Section */}

              <div className="provider-page-hero-visuals">
                {adsLoading ? (
                  <div className="ad-display-container" style={{ border: 'none' }}>
                    <div className="skeleton-bar skeleton-image"></div>
                  </div>
                ) : ads.length > 0 ? (
                  <div className="ad-container">
                    <div
                      className={`ad-display-container ${ads[currentAdIndex].action_url ? 'clickable' : ''}`}
                      onClick={() => {
                        // Only handle click if there's an action URL and no button (button handles its own clicks)
                        if (ads[currentAdIndex].action_url && !ads[currentAdIndex].button_text) {
                          handleAdClick(ads[currentAdIndex]);
                        }
                      }}
                      role={ads[currentAdIndex].action_url && !ads[currentAdIndex].button_text ? "button" : undefined}
                      aria-label={ads[currentAdIndex].action_url && !ads[currentAdIndex].button_text ? "Open advertisement" : undefined}
                      style={{ cursor: ads[currentAdIndex].action_url && !ads[currentAdIndex].button_text ? 'pointer' : 'default' }}
                    >
                      <img
                        src={ads[currentAdIndex].image_url}
                        alt={ads[currentAdIndex].description || 'Advertisement'}
                        className="ad-image"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="ad-display-container">
                    <div className="ad-placeholder">
                      <div className="ad-placeholder-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z" />
                        </svg>
                      </div>
                      <p>No ads available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Carousel indicators moved under the entire container */}
            {!adsLoading && ads.length > 1 && (
              <div className="ad-indicators">
                {ads.map((_, index) => (
                  <button
                    key={index}
                    className={`ad-indicator ${index === currentAdIndex ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentAdIndex(index);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="provider-error-message">
            <div className="error-content">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
              <button
                className="error-close"
                onClick={() => setError(null)}
                aria-label="Close error message"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* School Name Display */}


        {/* Accessible Providers */}
        <div className="provider-page-providers">
          <div className="provider-page-providers-header">
            {/* <h2 className="provider-page-providers-title">Accessible Providers</h2> */}
          </div>

          <div className="provider-page-providers-grid">
            {providersLoading ? (
              <div className="providers-loading">
                <div className="loading-spinner">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
                  </svg>
                </div>
                <p>Loading providers...</p>
              </div>
            ) : providers.length === 0 ? (
              <div className="no-providers">
                <div className="no-providers-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <h3>No Providers Available</h3>
                <p>There are currently no providers available. Please try again later.</p>
              </div>
            ) : (
              providers.map((provider) => (
                <div
                  key={provider.id}
                  className={`provider-card ${isBookrProvider(provider) ? 'bookr-provider' : ''} ${isHighlightsLibraryProvider(provider) ? 'highlights-library-provider' : ''} ${isOpenElmsProvider(provider) ? 'open-elms-provider' : ''}`}
                  onClick={() => handleProviderClickWrapper(provider)}
                >
                  {/* Replaced icon with real logo image */}
                  {provider.logo ? (
                    <div className="provider-card-logo">
                      <img
                        src={provider.logo}
                        alt={`${provider.name} logo`}
                        loading="lazy"
                        onError={(e) => {
                          // subtle fallback if URL is broken
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) fallback.classList.add('show');
                        }}
                      />
                      <div className="provider-logo-fallback">{provider.name?.[0] || 'P'}</div>
                    </div>
                  ) : (
                    <div className="provider-card-logo">
                      <div className="provider-logo-fallback">{provider.name?.[0] || 'P'}</div>
                    </div>
                  )}
                  <div className="provider-card-info">
                    {/* <h3 className="provider-card-name">{provider.name}</h3> */}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* BOOKR NEXT Warning Modal */}
      {showBookrNextWarning && (
        <div className="bookr-next-warning-overlay" onClick={(e) => {
          // Prevent closing when clicking overlay
          e.stopPropagation();
        }}>
          <div className="bookr-next-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bookr-next-warning-header">
              <div className="bookr-next-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <h2 className="bookr-next-warning-title">Attention Required</h2>
            </div>
            <div className="bookr-next-warning-body">
              <p className="bookr-next-warning-message">
                In order to use this provider you must download the app BOOKR Next.
              </p>
            </div>
            <div className="bookr-next-warning-actions">
              <button
                className="bookr-next-warning-button"
                onClick={handleBookrNextWarningAcknowledge}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderModal;
